import React, { useEffect, useState } from "react";
import { getPropertyAmenities } from "@services/krent.service";
import ktoast from "@services/toast.service";
import MultiSelectPropertyType from "@pages/Developers/Projects/multiselect-property";

interface Amenity {
  id?: string;
  title: string;
  displayTitle: string;
  icon: string;
  offers: string[];
}

interface PropertyUtilitiesProps {
  oldAmenities: any;
  onAmenitiesChange: (amenities: Amenity[]) => void;
}

const PropertyUtilities: React.FC<PropertyUtilitiesProps> = ({
  oldAmenities,
  onAmenitiesChange,
}) => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const abortController = new AbortController();
        const response = await getPropertyAmenities(abortController.signal);

        const transformedAmenities: Amenity[] = response.data
          .filter((item: any) => item.title !== "Amenities Checklist")
          .map((item: any) => ({
            id: item.id,
            displayTitle: item.title,
            title: item.title.toLowerCase().replace(/\s+/g, ""),
            icon: item.icon || "",
            offers: item.offers,
          }));

        setAmenities(transformedAmenities);

        if (oldAmenities && oldAmenities.length > 0) {
          const updatedSelectedAmenities = oldAmenities.map(
            (oldAmenity: any) => ({
              ...oldAmenity,
              displayTitle: oldAmenity.displayTitle || oldAmenity.title,
              title: oldAmenity.title.toLowerCase().replace(/\s+/g, ""),
            })
          );

          setSelectedAmenities(updatedSelectedAmenities);
        }

        setInitialLoadComplete(true);
      } catch (err: any) {
        ktoast.error(err.errors[0].message);
      }
    };

    fetchAmenities();
  }, []);

  useEffect(() => {
    if (initialLoadComplete) {
      onAmenitiesChange(selectedAmenities);
    }
  }, [selectedAmenities, initialLoadComplete, onAmenitiesChange]);

  const handleAmenityChange = (selectedValues: string[], amenity: Amenity) => {
    const updatedAmenity = {
      ...amenity,
      offers: selectedValues,
    };

    setSelectedAmenities((prev) => {
      const filteredPrev = prev.filter((item) => item.title !== amenity.title);

      if (selectedValues.length > 0) {
        return [...filteredPrev, updatedAmenity];
      }

      return filteredPrev;
    });
  };

  return (
    <div className="space-y-4">
      {amenities.map((amenity: Amenity) => {
        const options = amenity.offers.map((offer) => ({
          label: offer,
          value: offer,
        }));

        const selectedAmenity = selectedAmenities.find(
          (item) => item.title === amenity.title
        );

        const selectedOptions = selectedAmenity?.offers || [];

        return (
          <MultiSelectPropertyType
            key={amenity.id || amenity.title}
            title={amenity.displayTitle}
            options={options}
            selectedOptions={selectedOptions}
            setSelectedOptions={(selected) =>
              handleAmenityChange(selected, amenity)
            }
            placeholder={`Select ${amenity.displayTitle}...`}
          />
        );
      })}
    </div>
  );
};

export default PropertyUtilities;
